import React from "react";
import { useEffect, useState, useMemo, useRef } from "react";
import { formatDateFromTimestamp, formatDateTimeUtcFromTimestamp } from "../utils/dateUtils";
import FifoDetailRow from "./FifoDetailRow";
import { useLanguage } from "../contexts/LanguageContext.jsx";
import { t, getTaxCategoryLabel } from "../utils/translations";
import { fmtTokenAmountFixed, fmtEurPrice, fmtEurRow, fmtEurSummary } from "../utils/formatNumbers";
import { resolveV3TaxPlanName } from "../utils/v3taxPlanLabel.js";
import { normalizeSearchText } from "../utils/classificationLabel.js";
import V3TaxPurchaseTypeLabel from "./V3TaxPurchaseTypeLabel.jsx";
import VaiyansTypeLabel from "./VaiyansTypeLabel.jsx";
import { getVaiyansDisplay } from "../utils/vaiyansDisplay.js";
import MaskedEurCell from "./MaskedEurCell";
import {
  getPreviewTaxIssueKind,
  PreviewTaxIssueBadge,
  PreviewTaxIssueCell,
} from "./PreviewTaxIssue";
import { LayoutDashboard } from "lucide-react";
import { cn, tableTheme as T, analysisTone } from "./ui/analysisTableTheme.js";
import UnifiedTableEmptyState, { computeTableEmptyMetrics } from "./UnifiedTableEmptyState.jsx";
import {
  pickSectionTotals,
  currencySymbolOf,
  pickFiatPrice,
  pickFiatTotal,
  pickFiatTaxable,
  pickFiatBuy,
  pickFiatGrossProfit,
} from "../utils/fiatFields";
import {
  isVthoGasFeeRow,
  finiteMoneyOrNull,
} from "../utils/vthoGasFees.js";
import {
  shouldMaskPreviewEurRow,
  shouldShowPreviewSeparatorAfterRow,
} from "../utils/previewMode";
import PreviewTableNotice from "./PreviewTableNotice";
import PreviewModeSeparator from "./PreviewModeSeparator";
import TaxTreatmentValue from "./TaxTreatmentValue.jsx";
import {
  formatSyntheticVthoDisplay,
  isSyntheticVthoRow,
  isSyntheticVthoDailyRollup,
  parseSyntheticVthoDayKey,
  pickExportVthoTokenAmount,
  pickSyntheticVthoIncomeFiat,
} from "../utils/syntheticVthoDisplay.js";
import { applyVthoGeneratedDisplayRollup } from "../utils/rollupGeneratedVthoByDay.js";
import { rowMatchesTaxYear, resolveDisplayTaxYearLabel } from '../utils/taxYear.js';
import { buildTaxTableSectionData } from '../utils/taxTableSectionData.js';
import { JUICY_TAX_TYPE_FILTER_OPTIONS } from "../config/juicyFinanceClassifications.jsx";
import GasAbstractionFeeLabel from "./GasAbstractionFeeLabel.jsx";
import { getGasAbstractionFeePlainLabel } from "../utils/gasAbstractionFeeLabel.js";
import DexAggregatorExecutionCostLabel, {
  getDexAggregatorExecutionCostPlainLabel,
  isDexAggregatorExecutionCostClass,
} from "./DexAggregatorExecutionCostLabel.jsx";

function filterVthoYearRows(rows, effectiveYear, selectedCountry = 'DE') {
  const filtered = (rows || []).filter((tx) => {
    if (!tx.timestamp) return false;
    if (!effectiveYear) return false;
    return rowMatchesTaxYear(tx, selectedCountry, Number(effectiveYear));
  });
  return applyVthoGeneratedDisplayRollup(filtered);
}

function pickVthoRowTaxable(tx = {}) {
  if (isSyntheticVthoRow(tx) || String(tx.classification || "").toLowerCase() === "generated") {
    return pickSyntheticVthoIncomeFiat(tx) ?? pickFiatTaxable(tx);
  }
  return pickFiatTaxable(tx);
}

function VthoTxHashCell({ tx, language, pdfExportMode = false }) {
  const eventHint =
    pdfExportMode && (tx.clause_index != null || tx.clauseIndex != null || tx.event_index != null || tx.eventIndex != null || tx.log_index != null || tx.logIndex != null)
      ? `Event: C${tx.clause_index ?? tx.clauseIndex ?? "–"} / E${tx.event_index ?? tx.eventIndex ?? tx.log_index ?? tx.logIndex ?? "–"}`
      : null;

  if (!isSyntheticVthoRow(tx)) {
    return (
      <>
        {tx.hash || "—"}
        {eventHint ? (
          <div className="text-[10px] text-slate-500 print:text-slate-600 mt-0.5">{eventHint}</div>
        ) : null}
      </>
    );
  }

  const { label, range, wallets, splitReason } = formatSyntheticVthoDisplay(tx, language);

  return (
    <span title={tx.hash || undefined} className="block text-left">
      <span className="font-medium text-slate-700 print:text-black">{label}</span>
      {splitReason ? (
        <span className="mt-0.5 block text-xs font-normal text-slate-500 print:text-black">
          {splitReason}
        </span>
      ) : null}
      {(range || wallets) ? (
        <span className="mt-0.5 block text-xs font-normal text-slate-500 print:text-black">
          {range ? <span className="block">{range}</span> : null}
          {wallets ? <span className="block break-all">{wallets}</span> : null}
        </span>
      ) : null}
      {eventHint ? (
        <span className="mt-0.5 block text-[10px] text-slate-500 print:text-slate-600">{eventHint}</span>
      ) : null}
    </span>
  );
}

const HeaderIconBadge = ({ children, className = "" }) => (
  <div className={cn(T.headerMediaBadge, className)}>
    <div className="absolute inset-0 bg-gradient-to-br from-white via-white/85 to-slate-50/80" />
    <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
      <div className="absolute -top-2 -left-3 h-5 w-12 rotate-12 bg-white/50 blur-md" />
    </div>
    <div className="relative z-10 flex items-center justify-center">
      {children}
    </div>
  </div>
);


// 🚀 Helper-Funktion für Timestamp-Normalisierung
const toSec = (ts) => (Number(ts) > 1e12 ? Math.floor(Number(ts) / 1000) : Number(ts));

function getSyntheticDailyDay(row = {}) {
  const fromHash = parseSyntheticVthoDayKey(row.hash);
  if (fromHash) return fromHash;
  if (row.timestamp) {
    return new Date(toSec(row.timestamp) * 1000).toISOString().slice(0, 10);
  }
  return "";
}

function formatVthoDisplayDate(tx, { utc = false } = {}) {
  if (isSyntheticVthoDailyRollup(tx)) {
    const day = getSyntheticDailyDay(tx);
    return day ? `${day} UTC` : "UTC day";
  }
  if (!tx.timestamp) return "—";
  return utc
    ? formatDateTimeUtcFromTimestamp(toSec(tx.timestamp))
    : formatDateFromTimestamp(toSec(tx.timestamp));
}

function fmtTinyTokenAmount(value, decimals = 6) {
  const n = Number(value || 0);
  if (n === 0) return (0).toFixed(decimals);
  const min = 1 / Math.pow(10, decimals);
  if (Math.abs(n) < min) return `<${min.toFixed(decimals)}`;
  return fmtTokenAmountFixed(n, decimals);
}

function fmtTinyFiatRow(value) {
  const n = Number(value || 0);
  if (n === 0) return "0.00000";
  if (Math.abs(n) < 0.00001) return "<0.00001";
  return fmtEurRow(n);
}

function pickGasFeeCostBasis(row = {}) {
  const direct = Number(
    pickFiatBuy(row) ??
    row.totalCost ??
    row.costBasisFiat ??
    row.costBasisEUR
  );

  if (Number.isFinite(direct)) {
    return direct;
  }

  const legs = Array.isArray(row.fifoLegs)
    ? row.fifoLegs
    : Array.isArray(row.matchedInflows)
      ? row.matchedInflows
      : [];

  let total = 0;
  let hasUsableLeg = false;

  for (const leg of legs) {
    if (
      leg?.costBasisUnknown === true ||
      leg?.cost_basis_unknown === true ||
      leg?.pool_empty === true
    ) {
      continue;
    }

    const qty = Number(
      leg.qty ??
      leg.quantity ??
      leg.amount ??
      0
    );

    const unitCost = Number(
      leg.unitCostFiat ??
      leg.unit_cost_eur ??
      leg.unit_cost ??
      leg.euroPrice ??
      0
    );

    if (
      Number.isFinite(qty) &&
      Number.isFinite(unitCost)
    ) {
      total += qty * unitCost;
      hasUsableLeg = true;
    }
  }

  return hasUsableLeg ? total : null;
}

function pickGasFeeGainLoss(row = {}) {
  const direct = Number(
    pickFiatGrossProfit(row) ??
    row.profitFiat ??
    row.profitEUR
  );

  if (Number.isFinite(direct)) {
    return direct;
  }

  const proceeds = Number(pickFiatTotal(row));
  const costBasis = Number(pickGasFeeCostBasis(row));

  if (
    Number.isFinite(proceeds) &&
    Number.isFinite(costBasis)
  ) {
    return proceeds - costBasis;
  }

  return null;
}

function getGasFeeRowKey(row = {}, index = 0) {
  return [
    row.eventId,
    row.event_id,
    row.exportEventKey,
    row.eventKey,
    row.hash,
    row.timestamp,
    row.amount,
    index,
  ]
    .filter((value) => value !== null && value !== undefined && value !== "")
    .join("|");
}

function getGasFeeLegs(row = {}) {
  if (Array.isArray(row.fifoLegs) && row.fifoLegs.length > 0) {
    return row.fifoLegs;
  }
  if (Array.isArray(row.matchedInflows) && row.matchedInflows.length > 0) {
    return row.matchedInflows;
  }
  return [];
}

function GasFeeSummaryTable({
  rows = [],
  summary = {},
  expanded = false,
  onToggle,
  expandedFeeKey = null,
  onToggleFee,
  language = "de",
  currencyCode = "EUR",
  currencySymbol = "€",
  pdfExportMode = false,
  isPreviewMode = false,
  isEditMode = false,
  yearLabel = "",
  selectedCountry = "DE",
  renderDetailColgroup,
  renderDetailThead,
}) {
  if (!rows.length) return null;

  const showDetails = pdfExportMode || expanded;

  const renderMoney = (
    value,
    formatter = fmtEurSummary
  ) => {
    if (isPreviewMode) {
      return (
        <MaskedEurCell
          fullWidth
          currencySymbol={currencySymbol}
          currencyCode={currencyCode}
          language={language}
        />
      );
    }

    const n = finiteMoneyOrNull(value);

    return n === null
      ? "—"
      : `${currencySymbol} ${formatter(n)}`;
  };

  return (
    <div
      className={cn(
        T.tableWrap,
        "mx-6 mt-4 overflow-hidden border-amber-200/90 bg-white",
        "shadow-[0_10px_30px_rgba(180,83,9,0.07)] ring-1 ring-amber-100/80",
        "print:mx-0 print:mt-3 print:rounded-none print:border-slate-300 print:shadow-none print:ring-0"
      )}
    >
      <table className={cn(T.table, "w-full table-fixed")}>
        <colgroup>
          <col style={{ width: "18%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "13%" }} />
        </colgroup>

        <thead className="bg-gradient-to-r from-amber-300 via-yellow-300 to-amber-400 [&_th]:!border-amber-400/40 [&_th]:!text-slate-900 [&_th_span]:!text-slate-900 print:bg-amber-100">
          <tr>
            <th className={cn(T.th, "text-left")}>
              <span className={cn(T.thLabel, "v3-th-label-form")}>
                {t("type", language)}
              </span>
            </th>

            <th className={cn(T.th, "text-center")}>
              <span className={cn(T.thLabel, "v3-th-label-form")}>
                {language === "en"
                  ? "Transactions"
                  : "Transaktionen"}
              </span>
            </th>

            <th className={cn(T.th, "text-center")}>
              <span className={cn(T.thLabel, "v3-th-label-form")}>
                {t("token", language)}
              </span>
            </th>

            <th className={cn(T.th, "text-center")}>
              <span className={cn(T.thLabel, "v3-th-label-form")}>
                {t("amount", language)}
              </span>
            </th>

            <th className={cn(T.th, "text-center")}>
              <span className={T.thInnerCenter}>
                <span className={cn(T.thLabel, "v3-th-label-form")}>
                  {language === "en" ? "Avg. Price" : "Ø Kurs"}
                </span>
                <span className={cn(T.thSub, "v3-th-sub-form")}>
                  {currencyCode}
                </span>
              </span>
            </th>

            <th className={cn(T.th, "text-center")}>
              <span className={T.thInnerCenter}>
                <span className={cn(T.thLabel, "v3-th-label-form")}>
                  {t("total", language)}
                </span>
                <span className={cn(T.thSub, "v3-th-sub-form")}>
                  {currencyCode}
                </span>
              </span>
            </th>

            <th className={cn(T.th, "text-center")}>
              <span className={T.thInnerCenter}>
                <span className={cn(T.thLabel, "v3-th-label-form")}>
                  {t("taxableProfitShort", language)}
                </span>
                <span className={cn(T.thSub, "v3-th-sub-form")}>
                  ({currencyCode})
                </span>
              </span>
            </th>

            <th className={cn(T.th, "text-center")}>
              <span className={cn(T.thLabel, "v3-th-label-form")}>
                {language === "en" ? "Period" : "Zeitraum"}
              </span>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            className={cn(
              "bg-gradient-to-r from-amber-50/90 via-white to-yellow-50/80",
              !pdfExportMode &&
                "cursor-pointer transition-colors hover:from-amber-100/80 hover:via-amber-50/30 hover:to-yellow-100/70"
            )}
            onClick={pdfExportMode ? undefined : onToggle}
          >
            <td className={cn(T.td, "px-4 py-3")}>
              <button
                type="button"
                disabled={pdfExportMode}
                className="flex w-full items-center gap-2 text-left"
                onClick={(event) => {
                  event.stopPropagation();

                  if (!pdfExportMode) {
                    onToggle?.();
                  }
                }}
              >
                <span className="font-mono text-amber-700 print:hidden">
                  {showDetails ? "▼" : "▶"}
                </span>

                <span>
                  <span className="block font-semibold text-amber-900 print:text-black">
                    Gas Fee
                  </span>

                  <span className="block text-xs text-slate-600 print:text-black">
                    {language === "en"
                      ? "Combined annual value — individual transactions retained"
                      : "Jahressumme – Einzeltransaktionen bleiben vollständig erhalten"}
                  </span>
                </span>
              </button>
            </td>

            <td className={cn(T.td, "text-center font-mono")}>
              {summary.count || 0}
            </td>

            <td className={cn(T.td, "text-center font-mono")}>
              VTHO
            </td>

            <td className={cn(T.td, "text-center font-mono")}>
              {fmtTokenAmountFixed(
                summary.totalAmount || 0,
                6
              )}
            </td>

            <td className={cn(T.td, "text-center font-mono")}>
              {renderMoney(
                summary.weightedAveragePrice,
                fmtEurPrice
              )}
            </td>

            <td className={cn(T.td, "text-center font-mono")}>
              {renderMoney(summary.totalProceeds)}
            </td>

            <td className={cn(T.td, "text-center font-mono")}>
              {renderMoney(summary.totalTaxable)}
            </td>

            <td className={cn(T.td, "text-center")}>
              {yearLabel || "—"}
            </td>
          </tr>
        </tbody>
      </table>

      {showDetails ? (
        <div className="border-t border-violet-200 bg-white">
          <div className="overflow-x-auto">
            <table
              className={cn(
                T.table,
                "min-w-[1160px] table-fixed print:min-w-0 print:table-fixed"
              )}
            >
              {renderDetailColgroup?.()}
              {renderDetailThead?.()}

              <tbody className={T.tbody}>
                {rows.map((row, index) => {
                  const feeKey = getGasFeeRowKey(row, index);
                  const gasFeeLegs = getGasFeeLegs(row);
                  const hasFeeFifoDetails = gasFeeLegs.length > 0;
                  const isFeeExpanded = expandedFeeKey === feeKey;
                  const showFeeFifoDetails =
                    !pdfExportMode &&
                    isFeeExpanded &&
                    hasFeeFifoDetails;

                  const proceeds = finiteMoneyOrNull(
                    pickFiatTotal(row)
                  );

                  const taxableProfit = finiteMoneyOrNull(
                    pickVthoRowTaxable(row)
                  );

                  return (
                    <React.Fragment key={feeKey}>
                      <tr
                        className="group pdf-tx-main-row transition-colors duration-150 hover:bg-slate-50/80 print:hover:bg-transparent"
                      >
                        <td
                          className={cn(
                            T.td,
                            "v3-pdf-type-col w-[15%] print:w-[10.5%] min-w-0 align-top whitespace-normal break-words print:text-black"
                          )}
                        >
                          {!pdfExportMode && hasFeeFifoDetails ? (
                            <button
                              type="button"
                              onClick={() => onToggleFee?.(feeKey)}
                              className="inline-flex items-center gap-1 text-left font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              <span>{isFeeExpanded ? "▼" : "▶"}</span>
                              {String(row.classification || row.type || "").toLowerCase() === "gas_abstraction_fee" ? (
                                <GasAbstractionFeeLabel
                                  language={language}
                                  token={row.token || "VTHO"}
                                />
                              ) : (
                                <span>Gas Fee</span>
                              )}
                            </button>
                          ) : (
                            String(row.classification || row.type || "").toLowerCase() === "gas_abstraction_fee" ? (
                              <GasAbstractionFeeLabel
                                language={language}
                                token={row.token || "VTHO"}
                              />
                            ) : (
                              <span className="font-semibold text-blue-600">
                                Gas Fee
                              </span>
                            )
                          )}
                        </td>

                        <td
                          className={cn(
                            T.td,
                            T.mono,
                            "break-all whitespace-normal text-slate-700 print:text-black"
                          )}
                        >
                          <VthoTxHashCell
                            tx={row}
                            language={language}
                            pdfExportMode={pdfExportMode}
                          />
                        </td>

                        <td className="px-6 py-4 text-center text-gray-800 print:text-black">
                          {row.token || "VTHO"}
                        </td>

                        <td className="px-6 py-4 text-center font-mono text-gray-900 print:text-black">
                          {fmtTokenAmountFixed(
                            row.amount || 0,
                            6
                          )}
                        </td>

                        <td className="px-6 py-4 text-center font-mono text-gray-900 print:text-black">
                          {renderMoney(
                            pickFiatPrice(row),
                            fmtEurPrice
                          )}
                        </td>

                        <td className="px-6 py-4 text-center font-mono text-gray-900 print:text-black">
                          {renderMoney(
                            proceeds,
                            fmtEurRow
                          )}
                        </td>

                        <td className="px-6 py-4 text-center text-gray-900 print:text-black">
                          {isPreviewMode ? (
                            <MaskedEurCell
                              fullWidth
                              currencySymbol={currencySymbol}
                              currencyCode={currencyCode}
                              language={language}
                            />
                          ) : (
                            <TaxTreatmentValue
                              row={row}
                              language={language}
                              selectedCountry={selectedCountry}
                              formattedValue={renderMoney(
                                taxableProfit,
                                fmtEurRow
                              )}
                            />
                          )}
                        </td>

                        <td className="px-6 py-4 text-center text-gray-600 print:text-black">
                          {formatVthoDisplayDate(row, { utc: pdfExportMode })}
                        </td>
                      </tr>

                      {showFeeFifoDetails ? (
                        <FifoDetailRow
                          tx={row}
                          inflows={gasFeeLegs}
                          isEditMode={isEditMode}
                          nonTaxableInflows={{}}
                          onNonTaxableChange={() => {}}
                          selectedCountry={selectedCountry}
                          isPreviewMode={isPreviewMode}
                          maskRowEur={isPreviewMode}
                          pdfExportMode={pdfExportMode}
                          language={language}
                        />
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!pdfExportMode &&
          summary.incompleteCount > 0 ? (
            <div className="border-t border-amber-100 bg-amber-50 px-4 py-2 text-xs text-amber-800">
              {language === "en"
                ? `${summary.incompleteCount} fee transaction(s) require review because pricing or cost basis is incomplete.`
                : `${summary.incompleteCount} Gebühren-Transaktion(en) benötigen eine Prüfung, weil Kurs oder Anschaffungskosten unvollständig sind.`}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// 🚀 Helper: NFBC und swap_out als Disposals behandeln
const isSwapOutLike = (cls) => {
  const c = String(cls || "").toLowerCase();

  return (
    c === "swap_out" ||
    c === "nfbc_purchase" ||
    c === "v3tax_purchase" ||
    c === "pause_your_carbon_purchase" ||
    c === "vaiyans_vco_game_entry" ||
    c === "vaiyans_vco_prediction_entry" ||
    isDexAggregatorExecutionCostClass(c)
  );
};

// Helper: Soll diese Transaktion im PDF alle Details zeigen?
const isImportantFifoTx = (tx) => {
  const type = (tx.type || tx.event || "").toLowerCase();
  const classification = (tx.classification || "").toLowerCase();

  return (
    isSwapOutLike(type) ||
    isSwapOutLike(classification) ||
    type === "gas_abstraction_fee" ||
    classification === "gas_abstraction_fee" ||
    type === "nft_mint" ||
    classification === "nft_mint" ||
    type === "nft_galaxy_mint" ||
    classification === "nft_galaxy_mint" ||
    type === "nft_marketplace_purchase_payment" ||
    classification === "nft_marketplace_purchase_payment" ||
    type === "sale_payment" ||
    classification === "sale_payment"
  );
};

function isJuicyFinanceClass(cls) {
  return String(cls || "").trim().toLowerCase().startsWith("juicyfinance_");
}

function getJuicyFinanceDisplay(clsRaw, tokenRaw = "") {
  const cls = String(clsRaw || "").trim().toLowerCase();
  const token = String(tokenRaw || "").trim().toUpperCase();

  switch (cls) {
    case "juicyfinance_lending_income":
      return { title: "JuicyFinance", subtitle: token ? `${token} Lending Income` : "Lending Income" };
    case "juicyfinance_borrow_principal":
      return { title: "JuicyFinance", subtitle: token ? `${token} Borrow Principal` : "Borrow Principal" };
    case "juicyfinance_repay_principal":
      return { title: "JuicyFinance", subtitle: token ? `${token} Repay Principal` : "Repay Principal" };
    case "juicyfinance_borrow_interest_paid":
      return { title: "JuicyFinance", subtitle: token ? `${token} Borrow Interest Paid` : "Borrow Interest Paid" };
    case "juicyfinance_unmatched_withdrawal_principal":
      return {
        title: "JuicyFinance",
        subtitle: token
          ? `${token} Withdrawal (Principal unbekannt)`
          : "Withdrawal (Principal unbekannt)",
      };
    default:
      return {
        title: "JuicyFinance",
        subtitle: String(cls.replace(/^juicyfinance_/i, "") || "Interaction")
          .split(/\s+/)
          .join(" ")
          .replace(/_/g, " ")
          .split(/\s+/)
          .filter(Boolean)
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
          .join(" "),
      };
  }
}

function renderBlueAppType({
  icon,
  alt = "",
  title,
  subtitle,
  titleClass = "text-blue-600",
  subtitleClass = "text-blue-700/90",
}) {
  return (
    <div className={`flex items-start gap-2 font-semibold min-w-0 ${titleClass}`}>
      {icon ? (
        <img
          src={icon}
          alt={alt}
          className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
          draggable={false}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : null}
      <div className="min-w-0 leading-tight">
        <div className="whitespace-normal break-words">{title}</div>
        <div className={`whitespace-normal break-words text-[11px] font-medium ${subtitleClass}`}>
          {subtitle}
        </div>
      </div>
    </div>
  );
}

// 🏷️ UI: Typ-Badges (inkl. Icon/Color)
const TYPE_META = {
  verocket_generated_vtho_claim: {
    label: { de: "VeRocket VTHO Claim", en: "VeRocket VTHO Claim" },
    className: "bg-blue-50 text-blue-700 border-blue-200",
    imgSrc: "/VeRocket.png",
  },
  thorhead_staking_rewards: {
    label: { de: "Thorhead Staking Rewards", en: "Thorhead Staking Rewards" },
    className: "bg-blue-50 text-blue-700 border-blue-200",
    imgSrc: "/thorhead.jpg",
  },
  vemugs_staking_rewards: {
    label: { de: "VeMugs Staking Rewards", en: "VeMugs Staking Rewards" },
    className: "bg-blue-50 text-blue-700 border-blue-200",
    imgSrc: "/vemugs.png",
  },
  vemugs_mint_reward: {
    label: { de: "VeMugs Mint Reward", en: "VeMugs Mint Reward" },
    className: "bg-blue-50 text-blue-700 border-blue-200",
    imgSrc: "/vemugs.png",
  },
  vemugs_reward_distribution: {
    label: { de: "VeMugs Reward Distribution", en: "VeMugs Reward Distribution" },
    className: "bg-blue-50 text-blue-700 border-blue-200",
    imgSrc: "/vemugs.png",
  },
  vemugs_mint_gas_refund: {
    label: { de: "VeMugs Mint Gas Refund", en: "VeMugs Mint Gas Refund" },
    className: "bg-pink-50 text-pink-700 border-pink-200",
    imgSrc: "/vemugs.png",
  },
  vemugs_mint_zero_transfer: {
    label: {
      de: "VeMugs Mint 0 VTHO Placeholder",
      en: "VeMugs Mint 0 VTHO Placeholder",
    },
    className: "bg-pink-50 text-pink-700 border-pink-200",
    imgSrc: "/vemugs.png",
  },
};

const TypeBadge = ({ cls, token, language }) => {
  const key = String(cls || "").toLowerCase();

  if (key === "gas_abstraction_fee") {
    return <GasAbstractionFeeLabel language={language} token={token || "VTHO"} />;
  }

  if (isJuicyFinanceClass(key)) {
    const { title, subtitle } = getJuicyFinanceDisplay(key, token);
    return (
      <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
        <img
          src="/JF.png"
          alt="JuicyFinance"
          className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
          draggable={false}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <div className="min-w-0 leading-tight">
          <div className="whitespace-normal break-words">{title}</div>
          <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
            {subtitle}
          </div>
        </div>
      </div>
    );
  }

  const meta = TYPE_META[key];
  const fallback = cls ? String(cls) : "Unknown";
  if (!meta) return <span className="text-gray-800">{fallback}</span>;
  const label = meta.label?.[language] || meta.label?.en || fallback;

  return (
    <span className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-semibold ${meta.className}`}>
      <img src={meta.imgSrc} alt="" className="h-4 w-4 object-contain" draggable={false} />
      <span className="whitespace-nowrap">{label}</span>
    </span>
  );
};

function GeneratedVthoInfo({ language = "de", count = 0 }) {
  const isEnglish = language === "en";

  const title = isEnglish
    ? "Generated VTHO entries"
    : "Generierte VTHO-Einträge";

  const summary = isEnglish
    ? "VTHO generated from VET holdings is modeled as taxable income when it becomes relevant for the tax year."
    : "Aus VET-Beständen generiertes VTHO wird als steuerpflichtiges Einkommen modelliert, sobald es für das Steuerjahr relevant wird.";

  const details = isEnglish
    ? "These entries are synthetic calculation rows, not direct on-chain transactions. They help close FIFO periods correctly when VTHO leaves the wallet, for example through gas usage or other disposals."
    : "Diese Einträge sind synthetische Berechnungszeilen und keine direkten On-Chain-Transaktionen. Sie helfen dabei, FIFO-Zeiträume korrekt zu schließen, wenn VTHO die Wallet verlässt, zum Beispiel durch Gas-Nutzung oder andere Abgänge.";

  if (!count || count <= 0) return null;

  return (
    <details className="group mx-6 mt-3 overflow-hidden rounded-xl border border-slate-200/80 bg-white/75 shadow-[0_1px_0_rgba(255,255,255,0.95),0_8px_22px_rgba(15,23,42,0.025)] ring-1 ring-white/80 backdrop-blur-sm">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-2.5 text-xs">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-bold text-slate-800">{title}</span>
            <span className="rounded-full border border-emerald-100 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
              {count} {isEnglish ? "entries" : "Einträge"}
            </span>
          </div>
          <p className="mt-0.5 text-[11px] leading-relaxed text-slate-500">
            {summary}
          </p>
        </div>

        <span className="shrink-0 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold text-slate-500 transition-colors group-open:border-indigo-100 group-open:text-indigo-600">
          {isEnglish ? "Details" : "Details"}
        </span>
      </summary>

      <div className="border-t border-slate-100 bg-slate-50/45 px-4 pb-3 pt-2 text-[11px] leading-relaxed text-slate-500">
        {details}
      </div>
    </details>
  );
}

export default function VthoTaxTable({ 
  datasetId, // 🎯 NEU: Dataset-ID für Backend-API
  year, // 🎯 NEU: Jahr für Backend-API
  displayTaxYear,
  country = 'DE', // 🎯 NEU: Land für Backend-API
  vthoDataFromProps = [], // 🚀 NEU: VTHO-Daten direkt als Props
  vthoTotalsFromProps = {}, // 🚀 NEU: VTHO-Totals direkt als Props
  onTotalProfitCalculated, 
  onDataReady,
  isEditMode = false,
  selectedCountry = 'DE',
  pdfExportMode = false,
  isPreviewMode = false,
  enableExportCallback = true,
  previewMaskedTotalEUR = null,
  previewMaskedTotals = null,
}) {
  const { language } = useLanguage();
  const tone = analysisTone.indigo;
  const primaryTableWrapClass = "v3-primary-table-shell v3-primary-table-shell--violet";
  const primaryTableHeadClass = "v3-primary-table-head v3-primary-table-head--violet";

  const totalsMeta = useMemo(
    () => pickSectionTotals(vthoTotalsFromProps || {}, selectedCountry),
    [vthoTotalsFromProps, selectedCountry]
  );
  const currencyCode = totalsMeta.currency;
  const currencySymbol = currencySymbolOf(currencyCode);
  // 🎯 NEU: Backend-Daten State
  const [vthoData, setVthoData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // 🎯 NEU: UI State (nur für Anzeige)
  const [sortKey, setSortKey] = useState("timestamp");
  const [sortDirection, setSortDirection] = useState("desc");
  const [search, setSearch] = useState("");
  const [tokenFilter, setTokenFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [gasFeesExpanded, setGasFeesExpanded] = useState(false);
  const [expandedGasFeeKey, setExpandedGasFeeKey] = useState(null);

  const toggleRow = (index) => {
    if (pdfExportMode) return;
    setExpandedIndex((prev) => (prev === index ? null : index));
  };
  
  // 🚀 WICHTIG: Alle Hooks müssen vor frühen Returns kommen
  const effectiveYear = String(year || ""); // Kein Fallback auf aktuelles Jahr
  const yearLabel = resolveDisplayTaxYearLabel(year, selectedCountry, displayTaxYear);

  const pdfSectionData = useMemo(() => {
    if (!pdfExportMode) return null;
    return buildTaxTableSectionData(
      vthoDataFromProps,
      vthoTotalsFromProps,
      effectiveYear,
      selectedCountry,
      { filterRows: filterVthoYearRows }
    );
  }, [pdfExportMode, vthoDataFromProps, vthoTotalsFromProps, effectiveYear, selectedCountry]);

  const activeRows = pdfSectionData?.rows ?? vthoData.rows;
  const activeTotals = pdfSectionData?.totals ?? vthoData.totals;

  const gasFeeRows = useMemo(
    () =>
      (activeRows || [])
        .filter(isVthoGasFeeRow)
        .sort(
          (a, b) =>
            toSec(b.timestamp || b.ts || 0) -
            toSec(a.timestamp || a.ts || 0)
        ),
    [activeRows]
  );

  const regularVthoRows = useMemo(
    () =>
      (activeRows || []).filter(
        (row) => !isVthoGasFeeRow(row)
      ),
    [activeRows]
  );

  const gasFeeSummary = useMemo(() => {
    let totalAmount = 0;
    let totalProceeds = 0;
    let totalCostBasis = 0;
    let totalGainLoss = 0;
    let totalTaxable = 0;
    let incompleteCount = 0;

    for (const row of gasFeeRows) {
      const amount = Number(row.amount || 0);

      const proceeds = finiteMoneyOrNull(
        pickFiatTotal(row)
      );

      const costBasis = finiteMoneyOrNull(
        pickGasFeeCostBasis(row)
      );

      const gainLoss = finiteMoneyOrNull(
        pickGasFeeGainLoss(row)
      );

      const taxable = finiteMoneyOrNull(
        pickVthoRowTaxable(row)
      );

      totalAmount += Number.isFinite(amount)
        ? amount
        : 0;

      if (proceeds !== null) {
        totalProceeds += proceeds;
      }

      if (costBasis !== null) {
        totalCostBasis += costBasis;
      }

      if (gainLoss !== null) {
        totalGainLoss += gainLoss;
      }

      if (taxable !== null) {
        totalTaxable += taxable;
      }

      if (
        proceeds === null ||
        costBasis === null ||
        gainLoss === null
      ) {
        incompleteCount += 1;
      }
    }

    return {
      count: gasFeeRows.length,
      totalAmount,
      totalProceeds,
      totalCostBasis,
      totalGainLoss,
      totalTaxable,
      weightedAveragePrice:
        totalAmount > 0
          ? totalProceeds / totalAmount
          : 0,
      incompleteCount,
    };
  }, [gasFeeRows]);

  useEffect(() => {
    setGasFeesExpanded(false);
    setExpandedGasFeeKey(null);
  }, [effectiveYear, selectedCountry]);

  useEffect(() => {
    if (expandedGasFeeKey == null) return;
    const exists = gasFeeRows.some(
      (row, index) => getGasFeeRowKey(row, index) === expandedGasFeeKey
    );
    if (!exists) {
      setExpandedGasFeeKey(null);
    }
  }, [gasFeeRows, expandedGasFeeKey]);
  
  // 🚨 WICHTIG: Ref um mehrfache Ausführung zu verhindern
  const hasProcessedDataRef = useRef(false);

  // 🎯 NEU: Backend-API aufrufen mit Year-Filter
  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!datasetId) {
                return;
      }
      
      // 🚀 WICHTIG: Verhindere mehrfache Aufrufe
      if (loading) {
                return;
      }

      // 🚨 WICHTIG: Warte bis Backend-Analyse fertig ist
            
      // 🚨 WICHTIG: Verhindere mehrfache Ausführung, aber erlaube Updates
      if (hasProcessedDataRef.current && (vthoDataFromProps?.length || 0) > 0) {
                        // Nur überspringen wenn wir bereits Daten haben UND keine neuen Props-Daten
        if (!vthoDataFromProps || vthoDataFromProps.length === 0) {
          return;
        }
                hasProcessedDataRef.current = false; // Reset für neue Verarbeitung
      }

      // 🚨 WICHTIG: Verwende Props-Daten wenn verfügbar, sonst API-Aufruf
            
      if (vthoDataFromProps && vthoDataFromProps.length > 0) {
        hasProcessedDataRef.current = true;
        
          // Filtere VTHO-Daten für das gewählte Jahr
          const yearRows = filterVthoYearRows(vthoDataFromProps, effectiveYear, selectedCountry);
        
                
        // 📦 Backend-Totals verwenden (Backend-Truth) - keine Frontend-Berechnung mehr!
        const m = pickSectionTotals(vthoTotalsFromProps || {}, selectedCountry);
        const totals = {
          swapProfit: m.disposalNet,
          otherIncome: m.otherIncome,
          totalTaxable: m.totalTaxable,
          swapGain: m.disposalGain,
          swapLoss: m.disposalLoss,
        };
        
        if (!cancelled) {
          setVthoData({
            rows: yearRows,
            totals
          });
                  }
        
        return;
      }
      
      // 🚨 WICHTIG: KEINE API-AUFRUFE MEHR - verhindert endlose Loops!
            return;
      
      // 🚨 DEAKTIVIERT: API-Aufruf würde Backend-Analyse neu auslösen
            
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`/api/v1/tax/vtho?datasetId=${datasetId}&year=${effectiveYear}&country=${country}`, {
          credentials: 'include'
        });
        const data = await response.json();
        
        if (data.success) {
          // Alle Zeilen vom Backend (alle Jahre)
          const allRows = data.data?.rows ?? [];
                    
          // --- Year range (Europe/Berlin) ---
          // Wir setzen die Grenzen in Lokaltime Berlin (+01 im Winter).
          // So wird der Steuerjahreswechsel lokal korrekt getroffen.
          const startTsSec = Math.floor(new Date(`${effectiveYear}-01-01T00:00:00+01:00`).getTime() / 1000);
          const endTsSec   = Math.floor(new Date(`${effectiveYear}-12-31T23:59:59+01:00`).getTime() / 1000);
          
                    
          // Falls dein Backend gelegentlich ms liefert, abfedern:
          const toSec = (ts) => (ts > 1e12 ? Math.floor(ts / 1000) : ts);
          
          // Nur Zeilen des Zieljahrs zeigen (inkl. swap_out, generated etc.)
          const yearRows = applyVthoGeneratedDisplayRollup(
            allRows.filter((tx) => {
              const ts = toSec(tx.timestamp ?? 0);
              return ts >= startTsSec && ts <= endTsSec;
            })
          );
          
                    
          // 📦 Backend-Totals verwenden (Backend-Truth) - keine Frontend-Berechnung mehr!
          const m = pickSectionTotals(vthoTotalsFromProps || {}, selectedCountry);
          const totals = {
            swapProfit: m.disposalNet,
            otherIncome: m.otherIncome,
            totalTaxable: m.totalTaxable,
            swapGain: m.disposalGain,
            swapLoss: m.disposalLoss,
          };
          
          if (!cancelled) {
            setVthoData({
              rows: yearRows,
              totals
            });
                      }
        } else {
                    setError(data.error || 'Fehler beim Laden der VTHO-Daten');
        }
      } catch (error) {
                setError('Fehler beim Laden der VTHO-Daten');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [datasetId, year, country, selectedCountry, vthoTotalsFromProps]); // 🚀 WICHTIG: vthoDataFromProps entfernt um mehrfache Ausführung zu verhindern

  // 🚀 NEU: Reiner Props-Effekt für saubere Prop-Updates (keine Loops!)
  useEffect(() => {
    if (!Array.isArray(vthoDataFromProps)) return;


    // Filter Rows auf das gewählte Jahr (für Tabellen-Anzeige)
    const yearRows = filterVthoYearRows(vthoDataFromProps, effectiveYear, selectedCountry);

    // 📦 Backend-Totals verwenden (Backend-Truth) - keine Frontend-Berechnung mehr!
    const m = pickSectionTotals(vthoTotalsFromProps || {}, selectedCountry);
    const totals = {
      swapProfit: m.disposalNet,
      swapGain: m.disposalGain,
      swapLoss: m.disposalLoss,
      otherIncome: m.otherIncome,
      totalTaxable: m.totalTaxable,
    };

    setVthoData({ 
      rows: yearRows, 
      totals
    });
    hasProcessedDataRef.current = true;
    
  }, [vthoDataFromProps, vthoTotalsFromProps, effectiveYear, selectedCountry]);

  // 🎯 NEU: Summen an Parent melden
  useEffect(() => {
    if (isPreviewMode) return;
    if (onTotalProfitCalculated && vthoData.totals) {
      onTotalProfitCalculated({ 
        swapProfit: vthoData.totals.swapProfit, 
        otherIncome: vthoData.totals.otherIncome, 
        total: vthoData.totals.totalTaxable 
      });
    }
  }, [vthoData.totals]); // ✅ onTotalProfitCalculated entfernt um Endlosschleife zu vermeiden

  // 🎯 NEU: Export-Daten an Parent melden - direkt aus Props
  useEffect(() => {
    if (!onDataReady || isPreviewMode || !enableExportCallback || !vthoDataFromProps || vthoDataFromProps.length === 0) return;
    
    // Filtere VTHO-Daten für das gewählte Jahr (gleiche Logik wie oben)
    const yearRows = filterVthoYearRows(vthoDataFromProps, effectiveYear, selectedCountry);
    
    // 🔍 DEBUG: Prüfe VTHO-Daten
                if (yearRows.length > 0) {
                } else {
          }
    
    const csvLocale =
      String(selectedCountry || "DE").toUpperCase().includes("US")
        ? "en-US"
        : "de-DE";

    const exportable = yearRows.map(r => ({
      type: r.classification,
      token: 'VTHO',
      amount: pickExportVthoTokenAmount(r),
      fiatPrice: pickFiatPrice(r),
      totalFiat: pickFiatTotal(r),
      taxableProfitFiat: pickVthoRowTaxable(r) ?? 0,
      euroPrice: pickFiatPrice(r) ?? r.euroPrice ?? null,
      totalEUR: pickFiatTotal(r) ?? r.euroAmount ?? null,
      fifoProfitEUR: pickVthoRowTaxable(r) ?? r.taxableProfit ?? r.taxableProfitEUR ?? 0,
      hash: r.hash || '',
      date: r.timestamp 
        ? new Date(toSec(r.timestamp) * 1000).toLocaleString(csvLocale, {
            day: "2-digit",
            month: "2-digit", 
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          }).replace(",", "")
        : ''
    }));
    onDataReady(exportable);
  }, [vthoDataFromProps, effectiveYear, selectedCountry, isPreviewMode, onDataReady, enableExportCallback]);
  // 🎯 NEU: Einfache Anzeige-Logik (keine Berechnung mehr!)
  const processedData = useMemo(() => {
    if (
      !regularVthoRows ||
      regularVthoRows.length === 0
    ) {
      return {
        transactions: [],
        totalTaxable:
          activeTotals?.totalTaxable || 0,
        swapProfit:
          activeTotals?.swapProfit || 0,
        otherIncome:
          activeTotals?.otherIncome || 0,
      };
    }

    const transactions =
      regularVthoRows.map((row) => ({
        ...row,
        transactionType:
          isSwapOutLike(
            row.classification ||
            row.type ||
            ""
          )
            ? "swap"
            : "other",
      }));

    return {
      transactions,
      totalTaxable:
        activeTotals?.totalTaxable || 0,
      swapProfit:
        activeTotals?.swapProfit || 0,
      otherIncome:
        activeTotals?.otherIncome || 0,
    };
  }, [regularVthoRows, activeTotals]);

  // 🎯 NEU: Loading State - nach allen Hooks!

  // 🎯 NEU: Error State
  if (error) {
    return (
      <section className={cn(T.shell, "print:mt-0 report-section")}>
        <div className={cn(T.header, T.headerRail, tone.rail)}>
          <div className={T.headerInner}>
            <div className={T.headerMain}>
              <h2 className={T.title}>
                {t("vtho", language)} {t("taxTransactions", language)} – Error
              </h2>
            </div>
          </div>
        </div>
        <div className="px-6 py-8 text-center">
          <div className="text-red-600 text-lg">❌ {error}</div>
        </div>
      </section>
    );
  }

  // 🎯 NEU: Defensive Sortierung und Filterung
  const getVal = (tx, key) => {
    if (key === "total") return tx.euroAmount ?? 0;
    return tx[key] ?? 0;
  };

  const sorted = useMemo(() => {
    const data = processedData.transactions ?? [];
    return [...data].sort((a, b) => {
      const A = getVal(a, sortKey);
      const B = getVal(b, sortKey);
      if (typeof A === "string" && typeof B === "string") {
        return sortDirection === "asc" ? A.localeCompare(B) : B.localeCompare(A);
      }
      const diff = Number(A) - Number(B);
      return sortDirection === "asc" ? diff : -diff;
    });
  }, [processedData.transactions, sortKey, sortDirection]);

  const filtered = useMemo(() => {
    return sorted.filter((tx) => {
      const matchToken = tokenFilter === "ALL" || tx.token === tokenFilter;
      // Filter robust (case-insensitive)
      const matchType =
        typeFilter === "ALL" ||
        String(tx.classification || "").toLowerCase() === String(typeFilter || "").toLowerCase();
      const syntheticDisplay = isSyntheticVthoRow(tx)
        ? formatSyntheticVthoDisplay(tx, language)
        : null;
      const syntheticHashLabel = syntheticDisplay?.full || "";
      const searchNormalized = normalizeSearchText(search);
      const matchSearch = search === "" || [
        tx.classification || "",
        tx.hash || "",
        syntheticHashLabel,
        String(tx.amount || ""),
        String(tx.euroPrice || ""),
        tx.token || ""
      ].some(field => field.toLowerCase().includes(search.toLowerCase())) ||
        (searchNormalized !== "" &&
          normalizeSearchText(tx.classification || tx.type || "").includes(
            searchNormalized
          ));
      
      return matchToken && matchType && matchSearch;
    });
  }, [sorted, search, tokenFilter, typeFilter, language]);

  useEffect(() => {
    setExpandedIndex(null);
  }, [sortKey, sortDirection, search, tokenFilter, typeFilter, filtered.length]);

  const emptyMetrics = useMemo(
    () =>
      computeTableEmptyMetrics({
        allRows: vthoDataFromProps,
        yearRows: activeRows,
        filteredRows: filtered,
        effectiveYear,
      }),
    [vthoDataFromProps, activeRows, filtered, effectiveYear]
  );

  const generatedYearCount = useMemo(
    () =>
      (activeRows || []).filter(
        (tx) => String(tx.classification || "").toLowerCase() === "generated"
      ).length,
    [activeRows]
  );

  const generatedVetBalanceVisibleByIndex = useMemo(() => {
    const generatedIndexes = [];

    filtered.forEach((tx, index) => {
      if (String(tx.classification || "").toLowerCase() === "generated") {
        generatedIndexes.push(index);
      }
    });

    const visible = new Map();

    generatedIndexes.forEach((index, generatedPos) => {
      const tx = filtered[index];
      if (!tx?.vetAmount) return;

      const currentVetAmount = Number(tx.vetAmount || 0);

      if (generatedPos === 0) {
        visible.set(index, true);
        return;
      }

      const nextIndex = generatedIndexes[generatedPos + 1];

      if (nextIndex == null) {
        const prevIndex = generatedIndexes[generatedPos - 1];
        const prevVetAmount = Number(filtered[prevIndex]?.vetAmount || 0);
        visible.set(index, Math.abs(currentVetAmount - prevVetAmount) > 0.01);
        return;
      }

      const nextVetAmount = Number(filtered[nextIndex]?.vetAmount || 0);
      visible.set(index, Math.abs(currentVetAmount - nextVetAmount) > 0.01);
    });

    return visible;
  }, [filtered]);

const handleSort = (key) => {
  if (key === sortKey) {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
  } else {
    setSortKey(key);
    setSortDirection("asc");
  }
};

const getArrow = (key) => {
  if (key !== sortKey) return "";
  return sortDirection === "asc" ? " ↑" : " ↓";
};

  // 🎯 NEU: Keine komplexen Funktionen mehr - nur noch einfache Anzeige!

  const PdfTokenRowHeader = () => (
    <tr className="pdf-token-row-head">
      <th className={cn(T.th, "v3-pdf-type-col text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("type", language)}</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("txHashColumn", language)}</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("token", language)}</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("amount", language)}</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("price", language)}</span>
          <span className={cn(T.thSub, "v3-th-sub-fix")}>{currencyCode}</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("total", language)}</span>
          <span className={cn(T.thSub, "v3-th-sub-fix")}>{currencyCode}</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("taxableProfitShort", language)}</span>
          <span className={cn(T.thSub, "v3-th-sub-fix")}>({currencyCode})</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("date", language)}</span>
        </span>
      </th>
    </tr>
  );

  const renderDesktopColgroup = () => (
    <colgroup>
      <col style={{ width: "10.5%" }} />
      <col style={{ width: "26.3%" }} />
      <col style={{ width: "10.5%" }} />
      <col style={{ width: "10.5%" }} />
      <col style={{ width: "10.5%" }} />
      <col style={{ width: "10.5%" }} />
      <col style={{ width: "10.5%" }} />
      <col style={{ width: "10.5%" }} />
    </colgroup>
  );

  const renderDesktopThead = ({
    sortable = true,
  } = {}) => (
    <thead
      className={cn(
        T.thead,
        primaryTableHeadClass,
        "print:bg-white"
      )}
    >
      <tr>
        <th
          className={cn(
            T.th,
            "v3-pdf-type-col w-[15%] print:w-[10.5%] print:text-black",
            sortable && "cursor-pointer"
          )}
          onClick={
            sortable
              ? () => handleSort("classification")
              : undefined
          }
        >
          <span className={T.thInnerCenter}>
            <span
              className={cn(
                T.thLabel,
                "v3-th-label-form"
              )}
            >
              {t("type", language)}
              {sortable
                ? getArrow("classification")
                : ""}
            </span>
          </span>
        </th>

        <th
          className={cn(
            T.th,
            "w-[26.25%] print:w-[26.3%] print:text-black",
            sortable && "cursor-pointer"
          )}
          onClick={
            sortable
              ? () => handleSort("hash")
              : undefined
          }
        >
          <span className={T.thInnerCenter}>
            <span
              className={cn(
                T.thLabel,
                "v3-th-label-form"
              )}
            >
              {t("txHashColumn", language)}
              {sortable ? getArrow("hash") : ""}
            </span>
          </span>
        </th>

        <th
          className={cn(
            T.th,
            "w-[10.5%] text-center print:text-black"
          )}
        >
          <span className={T.thInnerCenter}>
            <span
              className={cn(
                T.thLabel,
                "v3-th-label-form"
              )}
            >
              {t("token", language)}
            </span>
          </span>
        </th>

        <th
          className={cn(
            T.th,
            "w-[10.5%] text-center print:text-black",
            sortable && "cursor-pointer"
          )}
          onClick={
            sortable
              ? () => handleSort("amount")
              : undefined
          }
        >
          <span className={T.thInnerCenter}>
            <span
              className={cn(
                T.thLabel,
                "v3-th-label-form"
              )}
            >
              {t("amount", language)}
              {sortable ? getArrow("amount") : ""}
            </span>
          </span>
        </th>

        <th
          className={cn(
            T.th,
            "w-[10.5%] text-center print:text-black",
            sortable && "cursor-pointer"
          )}
          onClick={
            sortable
              ? () => handleSort("euroPrice")
              : undefined
          }
        >
          <span className={T.thInnerCenter}>
            <span
              className={cn(
                T.thLabel,
                "v3-th-label-form"
              )}
            >
              {t("price", language)}
              {sortable
                ? getArrow("euroPrice")
                : ""}
            </span>

            <span
              className={cn(
                T.thSub,
                "v3-th-sub-form"
              )}
            >
              {currencyCode}
            </span>
          </span>
        </th>

        <th
          className={cn(
            T.th,
            "w-[10.5%] text-center print:text-black",
            sortable && "cursor-pointer"
          )}
          onClick={
            sortable
              ? () => handleSort("total")
              : undefined
          }
        >
          <span className={T.thInnerCenter}>
            <span
              className={cn(
                T.thLabel,
                "v3-th-label-form"
              )}
            >
              {t("total", language)}
              {sortable ? getArrow("total") : ""}
            </span>

            <span
              className={cn(
                T.thSub,
                "v3-th-sub-form"
              )}
            >
              {currencyCode}
            </span>
          </span>
        </th>

        <th
          className={cn(
            T.th,
            "w-[10.5%] text-center print:text-black"
          )}
        >
          <span className={T.thInnerCenter}>
            <span
              className={cn(
                T.thLabel,
                "v3-th-label-form"
              )}
            >
              {t(
                "taxableProfitShort",
                language
              )}
            </span>

            <span
              className={cn(
                T.thSub,
                "v3-th-sub-form"
              )}
            >
              ({currencyCode})
            </span>
          </span>
        </th>

        <th
          className={cn(
            T.th,
            "w-[10.5%] text-center print:text-black",
            sortable && "cursor-pointer"
          )}
          onClick={
            sortable
              ? () => handleSort("timestamp")
              : undefined
          }
        >
          <span className={T.thInnerCenter}>
            <span
              className={cn(
                T.thLabel,
                "v3-th-label-form"
              )}
            >
              {pdfExportMode
                ? language === "en"
                  ? "Date (UTC)"
                  : "Datum (UTC)"
                : t("date", language)}
              {sortable
                ? getArrow("timestamp")
                : ""}
            </span>
          </span>
        </th>
      </tr>
    </thead>
  );

  const renderDesktopTableFooter = () => (
    <>
      <tr className={cn("font-semibold border-t", tone.footerPrimary, "print:bg-gray-200 print:text-black")}>
        <td colSpan={6} className="px-6 py-4 text-right print:text-black">
          {getTaxCategoryLabel("disposal", selectedCountry, language)} ({t("swaps", language)}) –{" "}
          {language === "en" ? `Sum ${yearLabel}` : `Summe Steuerjahr ${yearLabel}`}
          {gasFeeRows.length > 0
            ? (
                language === "en"
                  ? " — including gas fees"
                  : " — inklusive Gas Fees"
              )
            : ""}
          :
        </td>
        <td className="px-6 py-4 font-mono print:text-black">
          {isPreviewMode ? (
            <MaskedEurCell
              value={previewMaskedTotals?.disposal || `${currencySymbol} **.**`}
              fullWidth
              currencySymbol={currencySymbol}
              currencyCode={currencyCode}
              language={language}
            />
          ) : (
            <>
              {currencySymbol} {fmtEurSummary(processedData.swapProfit)}
            </>
          )}
        </td>
        <td className="px-6 py-4" />
      </tr>
      <tr className={cn("font-semibold border-t", tone.footerSecondary, "print:bg-gray-200 print:text-black")}>
        <td colSpan={6} className="px-6 py-4 text-right print:text-black">
          {getTaxCategoryLabel("other", selectedCountry, language)} ({t("generatedVthoAndStakingRewards", language)}) –{" "}
          {language === "en" ? `Sum ${yearLabel}` : `Summe Steuerjahr ${yearLabel}`}:
        </td>
        <td className="px-6 py-4 font-mono print:text-black">
          {isPreviewMode ? (
            <MaskedEurCell
              value={previewMaskedTotals?.other || `${currencySymbol} **.**`}
              fullWidth
              currencySymbol={currencySymbol}
              currencyCode={currencyCode}
              language={language}
            />
          ) : (
            <>
              {currencySymbol} {fmtEurSummary(processedData.otherIncome || 0)}
            </>
          )}
        </td>
        <td className="px-6 py-4" />
      </tr>
    </>
  );

  const renderDesktopTxFragment = (tx, index) => {
const maskRowEur = shouldMaskPreviewEurRow(index, filtered.length, isPreviewMode);
            const classification = (tx.classification || '').toLowerCase();
            const isV3TaxPurchase = classification === "v3tax_purchase";
            const isGenerated = classification === "generated";
            const isVeRocketClaim = classification === "verocket_generated_vtho_claim";
            const isSwapOutLikeResult = isSwapOutLike(classification);
            const isStargateStaking = classification === "stargate_staking_rewards";
            const isThorheadStaking = classification === "thorhead_staking_rewards";
            const isVeMugsStaking = classification === "vemugs_staking_rewards";
            const isVeMugsMintReward = classification === "vemugs_mint_reward";
            const isVeMugsRewardDistribution = classification === "vemugs_reward_distribution";
            const isVeMugsMintGasRefund = classification === "vemugs_mint_gas_refund";
            const isVeMugsMintZeroTransfer =
              classification === "vemugs_mint_zero_transfer";
            const isLegacyNodeRewards = classification === "node_legacy_rewards";
            const isFoundationBonus = classification === "foundation_bonus";
            const isGasAbstractionFee = classification === "gas_abstraction_fee";
            const isDexExecutionCost = isDexAggregatorExecutionCostClass(classification);
            const vaiyansDisplay = getVaiyansDisplay(classification);
            const isJuicyFinance = isJuicyFinanceClass(classification);

            const hasFifoDetails =
              (Array.isArray(tx.fifoLegs) && tx.fifoLegs.length > 0) ||
              (Array.isArray(tx.matchedInflows) && tx.matchedInflows.length > 0);

            // 🔧 Preview: Button auch anzeigen wenn keine fifoLegs (werden vom Backend entfernt, aber Details sollen trotzdem sichtbar sein)
            const shouldShowButton =
              hasFifoDetails ||
              isV3TaxPurchase ||
              isGasAbstractionFee ||
              Boolean(vaiyansDisplay) ||
              (isPreviewMode && isSwapOutLikeResult);

            const showDetails = pdfExportMode
              ? hasFifoDetails && isImportantFifoTx(tx)
              : expandedIndex === index &&
                (isSwapOutLikeResult || isGasAbstractionFee || Boolean(vaiyansDisplay)) &&
                (hasFifoDetails || isPreviewMode || isV3TaxPurchase || isGasAbstractionFee || Boolean(vaiyansDisplay));
            
            const isExpanded = expandedIndex === index;
            
            const getLabel = () => {
              if (isDexExecutionCost) {
                return getDexAggregatorExecutionCostPlainLabel(tx);
              }

              if (vaiyansDisplay) {
                return `${vaiyansDisplay.title} - ${vaiyansDisplay.subtitle}`;
              }

              if (classification === "nfbc_purchase") {
                return language === "de"
                  ? "NFBC Kauf"
                  : "NFBC Purchase";
              }

              if (classification === "v3tax_purchase") {
                const plan = resolveV3TaxPlanName(tx);

                if (plan) {
                  return `V3Tax ${plan}`;
                }

                return language === "de"
                  ? "V3Tax Kauf"
                  : "V3Tax purchase";
              }

              if (classification === "pause_your_carbon_purchase") {
                return "Pause Your Carbon Purchase";
              }

              return "Swap_out";
            };
            const pdfTypeLabel = vaiyansDisplay
              ? `${vaiyansDisplay.title} - ${vaiyansDisplay.subtitle}`
              : isSwapOutLikeResult
              ? getLabel()
              : isGasAbstractionFee
                ? getGasAbstractionFeePlainLabel(language)
              : classification === "airdrop"
                ? "X2Earn Reward"
                : classification === "verocket_generated_vtho_claim"
                  ? "VeRocket Generated VTHO Claim"
                  : isVeMugsStaking
                    ? "VeMugs Staking Rewards"
                  : isVeMugsMintReward
                    ? "VeMugs Mint Reward"
                  : isVeMugsRewardDistribution
                    ? "VeMugs Reward Distribution"
                  : isVeMugsMintGasRefund
                    ? "VeMugs Mint Gas Refund"
                  : isVeMugsMintZeroTransfer
                    ? "VeMugs · Mint · 0 VTHO Placeholder"
                  : isGenerated
                    ? `${t('generated', language)} (${t('fromVetHolding', language)})`
                    : classification?.charAt(0).toUpperCase() + classification?.slice(1).toLowerCase();
            
            const showVetBalance =
              isGenerated && tx.vetAmount && generatedVetBalanceVisibleByIndex.get(index);

            const previewIssueKind = getPreviewTaxIssueKind(tx);
            const showPreviewIssueRow = isPreviewMode && !!previewIssueKind;

            return (
              <React.Fragment key={`${tx.hash || "nohash"}-${tx.timestamp || "notimestamp"}-${index}`}>
                {pdfExportMode && showDetails ? <PdfTokenRowHeader /> : null}
                <tr
                  className={`group pdf-tx-main-row transition-colors duration-150 ${
                    isGenerated
                      ? "bg-green-50 text-green-800 hover:bg-green-100"
                      : isVeRocketClaim
                        ? "bg-blue-50 text-blue-800 hover:bg-blue-100"
                        : "hover:bg-slate-50/80"
                  } print:hover:bg-transparent`}
                >
                  <td
                    className={cn(
                      T.td,
                      "v3-pdf-type-col w-[15%] print:w-[10.5%] min-w-0 align-top whitespace-normal break-words print:text-black"
                    )}
                  >
                    {pdfExportMode ? (
                      vaiyansDisplay ? (
                        <VaiyansTypeLabel classification={classification} />
                      ) : (
                        <span className="block w-full min-w-0 whitespace-normal break-words font-medium text-gray-700">
                          {pdfTypeLabel}
                        </span>
                      )
                    ) : vaiyansDisplay ? (
                      shouldShowButton ? (
                        <button
                          onClick={() => toggleRow(index)}
                          className="text-left hover:opacity-90 focus:outline-none"
                          disabled={pdfExportMode}
                          type="button"
                          title={
                            isPreviewMode
                              ? language === "en"
                                ? "Preview: fiat amounts locked, details still visible"
                                : "Preview: Fiat-Beträge gesperrt, Details sichtbar"
                              : undefined
                          }
                        >
                          <VaiyansTypeLabel
                            classification={classification}
                            leading={isExpanded ? "▼" : "▶"}
                          />
                        </button>
                      ) : (
                        <VaiyansTypeLabel classification={classification} />
                      )
                    ) : isSwapOutLikeResult ? (
                      classification === "v3tax_purchase" ? (
                        pdfExportMode ? (
                          <span className="block w-full min-w-0 whitespace-normal break-words font-medium text-gray-700">
                            {pdfTypeLabel}
                          </span>
                        ) : shouldShowButton ? (
                          <button
                            onClick={() => toggleRow(index)}
                            className="text-left hover:opacity-90 transition-colors duration-200"
                            title={
                              isPreviewMode
                                ? language === "en"
                                  ? "Preview: fiat amounts locked, details still visible"
                                  : "Preview: Fiat-Beträge gesperrt, Details sichtbar"
                                : undefined
                            }
                          >
                            <V3TaxPurchaseTypeLabel
                              tx={tx}
                              language={language}
                              leading={isExpanded ? "▼" : "▶"}
                            />
                          </button>
                        ) : (
                          <V3TaxPurchaseTypeLabel tx={tx} language={language} leading="▶" />
                        )
                      ) : shouldShowButton ? (
                          <button
                            onClick={() => toggleRow(index)}
                            className="block w-full min-w-0 whitespace-normal [overflow-wrap:anywhere] text-left text-blue-700 hover:text-blue-900 hover:underline transition-colors duration-200"
                          title={
                            isPreviewMode
                              ? language === "en"
                                ? "Preview: fiat amounts locked, details still visible"
                                : "Preview: Fiat-Beträge gesperrt, Details sichtbar"
                              : undefined
                          }
                        >
                          <span className="flex w-full min-w-0 items-start gap-1">
                            <span>{isExpanded ? "▼" : "▶"}</span>
                            {isDexExecutionCost ? (
                              <DexAggregatorExecutionCostLabel tx={tx} />
                            ) : (
                              <span className="min-w-0 whitespace-normal [overflow-wrap:anywhere]">{getLabel()}</span>
                            )}
                          </span>
                        </button>
                      ) : (
                        isDexExecutionCost ? (
                          <DexAggregatorExecutionCostLabel tx={tx} />
                        ) : (
                          <span className="flex w-full min-w-0 items-start gap-1 whitespace-normal [overflow-wrap:anywhere] text-blue-600 font-semibold">
                            <span>▶</span>
                            <span className="min-w-0 whitespace-normal [overflow-wrap:anywhere]">{getLabel()}</span>
                          </span>
                        )
                      )
                    ) : isGasAbstractionFee ? (
                      <button
                        onClick={() => toggleRow(index)}
                        className="block w-full min-w-0 whitespace-normal [overflow-wrap:anywhere] text-left hover:opacity-90 focus:outline-none"
                        disabled={pdfExportMode}
                        type="button"
                      >
                        <span className="flex w-full min-w-0 items-start gap-1">
                          <span className="mt-0.5 flex-shrink-0" aria-hidden>
                            {isExpanded ? "\u25bc" : "\u25b6"}
                          </span>
                          <GasAbstractionFeeLabel
                            language={language}
                            token={tx.token || "VTHO"}
                            className="min-w-0 whitespace-normal [overflow-wrap:anywhere]"
                          />
                        </span>
                      </button>
                    ) : isVeRocketClaim ? (
                      <TypeBadge cls="verocket_generated_vtho_claim" token={tx.token} language={language} />
                    ) : isJuicyFinance ? (
                      <TypeBadge cls={classification} token={tx.token} language={language} />
                    ) : isGenerated ? (
                      `${t('generated', language)} (${t('fromVetHolding', language)})`
                    ) : isStargateStaking ? (
                      renderBlueAppType({
                        icon: "/Stargate.png",
                        alt: "Stargate",
                        title: "Stargate",
                        subtitle: "Staking Rewards",
                      })
                    ) : isThorheadStaking ? (
                      renderBlueAppType({
                        icon: "/thorhead.jpg",
                        alt: "Thorhead",
                        title: "Thorhead",
                        subtitle: "Staking Rewards",
                      })
                    ) : isVeMugsStaking ? (
                      renderBlueAppType({
                        icon: "/vemugs.png",
                        alt: "VeMugs",
                        title: "VeMugs",
                        subtitle: "Staking Rewards",
                      })
                    ) : isVeMugsMintReward ? (
                      renderBlueAppType({
                        icon: "/vemugs.png",
                        alt: "VeMugs",
                        title: "VeMugs",
                        subtitle: "Mint Reward",
                      })
                    ) : isVeMugsRewardDistribution ? (
                      renderBlueAppType({
                        icon: "/vemugs.png",
                        alt: "VeMugs",
                        title: "VeMugs",
                        subtitle: "Reward Distribution",
                      })
                    ) : isVeMugsMintGasRefund ? (
                      renderBlueAppType({
                        icon: "/vemugs.png",
                        alt: "VeMugs",
                        title: "VeMugs",
                        subtitle: "Mint Gas Refund",
                        titleClass: "text-pink-600",
                        subtitleClass: "text-pink-700/90",
                      })
                    ) : isVeMugsMintZeroTransfer ? (
                      renderBlueAppType({
                        icon: "/vemugs.png",
                        alt: "VeMugs",
                        title: "VeMugs",
                        subtitle: "Mint · 0 VTHO Placeholder",
                        titleClass: "text-pink-600",
                        subtitleClass: "text-pink-700/90",
                      })
                    ) : isLegacyNodeRewards ? (
                      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60 font-semibold">
                        <span aria-hidden>📡</span>
                        <span>Legacy Node Rewards</span>
                      </span>
                    ) : isFoundationBonus ? (
                      <span className="inline-flex flex-col items-center gap-0.5 rounded-full px-2 py-1 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60 font-semibold">
                        <span className="inline-flex items-center gap-1">
                          <span aria-hidden>🏛️🔄</span>
                          <span>Foundation</span>
                        </span>
                        <span className="text-xs">Bonus/Swap</span>
                      </span>
                    ) : (
                      classification?.charAt(0).toUpperCase() + classification?.slice(1).toLowerCase()
                    )}
                  </td>
                  <td className={cn(T.td, T.mono, "break-all whitespace-normal text-slate-700 print:text-black")}>
                    <VthoTxHashCell tx={tx} language={language} pdfExportMode={pdfExportMode} />
                  </td>
                  <td className="px-6 py-4 text-center text-gray-800 print:text-black">{tx.token || "VTHO"}</td>
                  <td className="px-6 py-4 text-center font-mono text-gray-900 print:text-black">
                    {isSyntheticVthoRow(tx)
                      ? fmtTinyTokenAmount(tx.amount || 0, 6)
                      : fmtTokenAmountFixed(tx.amount || 0, 6)}
                  </td>
                  {showPreviewIssueRow ? (
                    <PreviewTaxIssueCell
                      language={language}
                      issueKind={previewIssueKind}
                      colSpan={3}
                      className="print:text-black"
                    />
                  ) : (
                    <>
                      <td className="px-6 py-4 text-center font-mono text-gray-900 print:text-black">
                        {maskRowEur ? (
                          <MaskedEurCell fullWidth currencySymbol={currencySymbol} currencyCode={currencyCode} language={language} />
                        ) : (
                          <>{currencySymbol} {fmtEurPrice(pickFiatPrice(tx) ?? 0)}</>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center font-mono text-gray-900 print:text-black">
                        {maskRowEur ? (
                          <MaskedEurCell fullWidth currencySymbol={currencySymbol} currencyCode={currencyCode} language={language} />
                        ) : (
                          <>{currencySymbol} {isSyntheticVthoRow(tx)
                            ? fmtTinyFiatRow(pickFiatTotal(tx) ?? 0)
                            : fmtEurRow(pickFiatTotal(tx) ?? 0)}</>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-gray-900 print:text-black">
                        {maskRowEur ? (
                          <MaskedEurCell fullWidth currencySymbol={currencySymbol} currencyCode={currencyCode} language={language} />
                        ) : (
                          <TaxTreatmentValue
                            row={tx}
                            language={language}
                            selectedCountry={selectedCountry}
                            formattedValue={`${currencySymbol} ${
                              isSyntheticVthoRow(tx)
                                ? fmtTinyFiatRow(pickVthoRowTaxable(tx) ?? 0)
                                : fmtEurRow(pickVthoRowTaxable(tx) ?? 0)
                            }`}
                          />
                        )}
                      </td>
                    </>
                  )}
                  <td className="px-6 py-4 text-center text-gray-600 print:text-black">
                    <div className="space-y-1">
                      <div>{formatVthoDisplayDate(tx, { utc: pdfExportMode })}</div>
                      {showVetBalance && (
                        <div className="text-xs text-blue-600 font-medium">
                          {t("vetHoldingBalanceLabel", language)} {fmtTokenAmountFixed(tx.vetAmount || 0, 6)}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>

                {showDetails && (
                  <FifoDetailRow 
                    tx={tx} 
                    inflows={tx.fifoLegs || tx.matchedInflows || []}
                    isEditMode={isEditMode}
                    nonTaxableInflows={{}}
                    onNonTaxableChange={() => {}}
                    selectedCountry={selectedCountry}
                    isPreviewMode={isPreviewMode}
                    maskRowEur={maskRowEur}
                    pdfExportMode={pdfExportMode}
                    language={language}
                  />
                )}
                <PreviewModeSeparator
                  show={shouldShowPreviewSeparatorAfterRow(index, filtered.length, isPreviewMode)}
                  colSpan={8}
                  language={language}
                  currencyCode={currencyCode}
                />
              </React.Fragment>
            );
  };

return (
  <section
    className={cn(
      T.shell,
      "print:mt-0 report-section print:shadow-none print:border-2 print:border-gray-300",
      pdfExportMode && "rounded-none border-0 bg-white shadow-none ring-0"
    )}
  >
    <div className={cn(T.header, T.headerRail, tone.rail, "print:border-slate-200")}>
      <div className={T.headerInner}>
        <div className={T.headerMain}>
          <span className={T.eyebrow}>
            <LayoutDashboard className={cn("h-3.5 w-3.5", tone.eyebrow)} />
            {t("taxYear", language)} {yearLabel}
          </span>
          <h2 className={cn(T.title, "print:text-black")}>
            {t("vtho", language)} {t("taxTransactions", language)}
          </h2>
          <p className={T.subtitle}>
            {language === "en"
              ? "Calculated VTHO disposal transactions and taxable income for the selected tax year"
              : "Berechnete VTHO-Veräußerungen und steuerpflichtige Einkünfte für das ausgewählte Steuerjahr"}
          </p>
        </div>
        {!pdfExportMode && (
          <div className={T.headerAsidePills}>
            <span className={cn(T.headerChipSoft, "text-violet-700 border-violet-200 bg-violet-50/80")}>
              <span className="h-2 w-2 rounded-full bg-violet-500" />
              <span>VTHO</span>
            </span>

            <HeaderIconBadge className="print:bg-white print:shadow-none print:ring-gray-300">
              <img
                src="/vtho-icon-aa.png"
                alt="VTHO"
                className="h-8 w-8 object-contain"
                draggable={false}
              />
            </HeaderIconBadge>
          </div>
        )}
      </div>
    </div>

    <GasFeeSummaryTable
      rows={gasFeeRows}
      summary={gasFeeSummary}
      expanded={gasFeesExpanded}
      onToggle={() => {
        setGasFeesExpanded((current) => {
          const next = !current;
          if (!next) {
            setExpandedGasFeeKey(null);
          }
          return next;
        });
      }}
      expandedFeeKey={expandedGasFeeKey}
      onToggleFee={(key) => {
        setExpandedGasFeeKey((current) =>
          current === key ? null : key
        );
      }}
      language={language}
      currencyCode={currencyCode}
      currencySymbol={currencySymbol}
      pdfExportMode={pdfExportMode}
      isPreviewMode={isPreviewMode}
      isEditMode={isEditMode}
      yearLabel={yearLabel}
      selectedCountry={selectedCountry}
      renderDetailColgroup={
        renderDesktopColgroup
      }
      renderDetailThead={() =>
        renderDesktopThead({
          sortable: false,
        })
      }
    />

    {!pdfExportMode && (
      <GeneratedVthoInfo language={language} count={generatedYearCount} />
    )}

    {!pdfExportMode && (
      <div className={T.toolbarWrap}>
        <div className={cn(T.toolbar, "flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between")}>
          <div className="flex-1 min-w-0">
            <input
              type="text"
              placeholder={`🔍 ${t("search", language)} ${t("type", language)}, ${t("transactionHash", language)}, ${t("amount", language)}, ${t("date", language)}...`}
              className={T.input}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className={T.countPill}>
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              <span className="font-semibold text-slate-800">{filtered.length}</span>
              <span>{t("transactions", language)}</span>
              {gasFeeRows.length > 0 ? (
                <span className="text-slate-500">
                  + {gasFeeRows.length}{" "}
                  {language === "en"
                    ? "gas fees summarized"
                    : "Gas Fees zusammengefasst"}
                </span>
              ) : null}
            </span>
            <select className={T.select} value={tokenFilter} onChange={(e) => setTokenFilter(e.target.value)}>
              <option value="ALL">🔁 {t("allTokens", language)}</option>
              <option value="VTHO">VTHO</option>
            </select>
            <select className={T.select} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="ALL">🔎 {t("allTypes", language)}</option>
              <option value="swap_out">🔄 Swap Out</option>
              <option value="vaiyans_vco_prediction_entry">
                🔮 Vaiyans VCO Prediction Entry
              </option>
              {JUICY_TAX_TYPE_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
              <option value="nfbc_purchase">📚 NFBC Purchase</option>
              <option value="stargate_staking_rewards">Stargate Staking Rewards</option>
              <option value="thorhead_staking_rewards">Thorhead Staking Rewards</option>
              <option value="vemugs_staking_rewards">VeMugs Staking Rewards</option>
              <option value="vemugs_mint_reward">VeMugs Mint Reward</option>
              <option value="vemugs_reward_distribution">VeMugs Reward Distribution</option>
              <option value="vemugs_mint_gas_refund">VeMugs Mint Gas Refund</option>
              <option value="vemugs_mint_zero_transfer">VeMugs Mint 0 VTHO Placeholder</option>
              <option value="node_legacy_rewards">📡 Legacy Node Rewards</option>
              <option value="foundation_bonus">🏛️🔄 Foundation Bonus/Swap</option>
              <option value="verocket_generated_vtho_claim">🟦 VeRocket VTHO Claims</option>
              <option value="generated">⚡ Generiert (aus VET Holding)</option>
            </select>
          </div>
        </div>
      </div>
    )}

    <PreviewTableNotice
      isPreviewMode={isPreviewMode}
      totalRows={filtered.length}
      language={language}
      currencyCode={currencyCode}
    />

    <div className={cn(T.tableBlock, "hidden md:block print:block")}>
      <div className={cn(T.tableWrap, primaryTableWrapClass)}>
      {filtered.length === 0 ? (
        <table className={cn(T.table, "min-w-[1160px] table-fixed print:min-w-0 print:table-fixed")}>
          {renderDesktopColgroup()}
          {renderDesktopThead()}
          <tbody className={T.tbody}>
            <tr>
              <td colSpan={8} className="px-6 py-10">
                <UnifiedTableEmptyState
                  language={language}
                  year={yearLabel}
                  scopeLabel="VTHO"
                  totalCount={emptyMetrics.totalCount}
                  yearCount={emptyMetrics.yearCount}
                  otherYearsCount={emptyMetrics.otherYearsCount}
                  hiddenByFilterCount={emptyMetrics.hiddenByFilterCount}
                  inTable
                />
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <table className={cn(T.table, "min-w-[1160px] table-fixed print:min-w-0 print:table-fixed")}>
          {renderDesktopColgroup()}
          {renderDesktopThead()}
          <tbody className={T.tbody}>
            {filtered.map((tx, index) => renderDesktopTxFragment(tx, index))}
            {renderDesktopTableFooter()}
          </tbody>
        </table>
      )}
      </div>
    </div>
  </section>
);
}
